import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { secondaryBtn } from "../pages/resilience/shared";

// Lessons learned back into policies (docs/POLICY-ADOPTION.md "Lessons learned", backend lib/policyShortfalls.ts).
//   * PolicyShortfallRaise — on a post-incident review, an internal audit finding or an auditor finding:
//     record that the review or finding showed a policy fell short, naming the policy.
//   * PolicyShortfallList — on a policy: its shortfalls, the version written or adopted since, and
//     closing one as addressed (by that version) or dismissed.
// Raising needs the source register's permission and closing documents:write; the server enforces both.

export type ShortfallSourceKind = "incident_review" | "internal_audit_finding" | "auditor_finding";

interface Shortfall {
  id: string; policyTemplateId: string; policyTitle: string; sourceKind: ShortfallSourceKind; sourceLabel: string; description: string;
  status: "open" | "addressed" | "dismissed"; raisedAt: string; reviewDueAt: string; resolvedAt: string | null; resolutionNote: string | null;
  addressedVersion: string | null; raisedByName: string | null; newerVersion: { kind: string; label: string; at: string } | null;
}
interface PolicyOption { templateId: string; title: string; state: string }

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };
const STATE_WORDS: Record<string, string> = { adopted: "adopted", adopted_superseded: "adopted, newer draft", draft_only: "draft only" };

function statusTag(s: Shortfall) {
  if (s.status === "addressed") return <StatusTag tone="met">Addressed</StatusTag>;
  if (s.status === "dismissed") return <StatusTag tone="none">Dismissed</StatusTag>;
  return <StatusTag tone={new Date(s.reviewDueAt).getTime() < Date.now() ? "gap" : "partial"}>Review due {fmtDateOnly(s.reviewDueAt)}</StatusTag>;
}

/** "Policy fell short" on a review or finding, with the shortfalls already recorded from it. */
export function PolicyShortfallRaise({ sourceKind, sourceId, canRaise }: { sourceKind: ShortfallSourceKind; sourceId: string; canRaise: boolean }) {
  const [rows, setRows] = useState<Shortfall[]>([]);
  const [open, setOpen] = useState(false);
  const [policies, setPolicies] = useState<PolicyOption[] | null>(null);
  const [policyId, setPolicyId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows((await api.get<{ shortfalls: Shortfall[] }>(`/policy-shortfalls?sourceKind=${sourceKind}&sourceId=${encodeURIComponent(sourceId)}`)).shortfalls); }
    catch { setRows([]); }
  }, [sourceKind, sourceId]);
  useEffect(() => { void load(); }, [load]);

  async function start() {
    setOpen(true); setError(null);
    if (!policies) {
      try { setPolicies((await api.get<{ policies: PolicyOption[] }>("/policy-shortfalls/policies")).policies); }
      catch (e) { setError(apiErrorMessage(e, "Could not load the policies.")); }
    }
  }

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post("/policy-shortfalls", { policyTemplateId: policyId, sourceKind, sourceId, description });
      setOpen(false); setPolicyId(""); setDescription(""); await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not record it.")); }
    finally { setBusy(false); }
  }

  if (!rows.length && !canRaise) return null;
  return (
    <div style={{ marginTop: 10 }}>
      {rows.map((s) => (
        <div key={s.id} style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
          <span>Policy fell short:</span>
          <Link to={`/templates?shortfall=${s.id}`} style={{ color: theme.color.ref }}>{s.policyTitle}</Link>
          {statusTag(s)}
        </div>
      ))}
      {canRaise && !open && (
        <button type="button" onClick={() => void start()} style={smallBtn} title="Record that this showed a policy did not say what to do, said the wrong thing, or could not be followed">
          A policy fell short
        </button>
      )}
      {open && (
        <div style={{ marginTop: 6, display: "grid", gap: 6 }}>
          <label style={text.hint} htmlFor={`psf-policy-${sourceId}`}>Which policy</label>
          <select id={`psf-policy-${sourceId}`} value={policyId} onChange={(e) => setPolicyId(e.target.value)} style={inputStyle}>
            <option value="">Choose a policy</option>
            {(policies ?? []).map((p) => <option key={p.templateId} value={p.templateId}>{p.title} ({STATE_WORDS[p.state] ?? p.state})</option>)}
          </select>
          {policies && !policies.length && <div style={text.hint}>No policy has been generated or adopted yet.</div>}
          <label style={text.hint} htmlFor={`psf-desc-${sourceId}`}>How it fell short (what it did not say, said wrongly, or could not be followed)</label>
          <textarea id={`psf-desc-${sourceId}`} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
          {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy || !policyId} onClick={() => void save()} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12, opacity: busy || !policyId ? 0.6 : 1 }}>Record shortfall</button>
            <button type="button" onClick={() => setOpen(false)} style={smallBtn}>Cancel</button>
          </div>
          <div style={text.hint}>The policy is flagged for review and its owner gets a review task. Nothing in the policy changes until someone revises it.</div>
        </div>
      )}
    </div>
  );
}

/** A policy's shortfalls, for its card on Policy templates. `highlight` is a shortfall id from ?shortfall=. */
export function PolicyShortfallList({ templateId, canClose, highlight }: { templateId: string; canClose: boolean; highlight?: string | null }) {
  const [rows, setRows] = useState<Shortfall[]>([]);
  const [closing, setClosing] = useState<{ id: string; action: "addressed" | "dismissed" } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows((await api.get<{ shortfalls: Shortfall[] }>(`/policy-shortfalls?policyTemplateId=${encodeURIComponent(templateId)}`)).shortfalls); }
    catch { setRows([]); }
  }, [templateId]);
  useEffect(() => { void load(); }, [load]);

  async function close() {
    if (!closing) return;
    setBusy(true); setError(null);
    try { await api.patch(`/policy-shortfalls/${closing.id}`, { action: closing.action, note }); setClosing(null); setNote(""); await load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not close it.")); }
    finally { setBusy(false); }
  }

  if (!rows.length) return null;
  const openCount = rows.filter((r) => r.status === "open").length;
  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--c-line)", paddingTop: 8 }}>
      <div style={{ ...text.label, marginBottom: 6 }}>Lessons learned{openCount ? ` · ${openCount} to address` : ""}</div>
      {rows.map((s) => (
        <div key={s.id} id={`shortfall-${s.id}`} style={{ padding: "6px 8px", marginBottom: 6, borderRadius: 3, border: `1px solid ${s.id === highlight ? "var(--c-accent)" : "var(--c-border)"}` }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
            <span style={{ ...text.body, fontWeight: 600 }}>{s.sourceLabel}</span>
            {statusTag(s)}
          </div>
          <div style={{ ...text.body, whiteSpace: "pre-wrap", marginTop: 4 }}>{s.description}</div>
          <div style={{ ...text.hint, marginTop: 4 }}>
            Recorded {fmtDate(s.raisedAt)}{s.raisedByName ? ` by ${s.raisedByName}` : ""}.
            {s.status === "open" && (s.newerVersion ? ` ${s.newerVersion.label} is newer: confirm it addresses this.` : " No version has been written or adopted since.")}
            {s.status !== "open" && s.resolutionNote && ` ${s.status === "addressed" ? `Addressed by ${s.addressedVersion}` : "Dismissed"}: ${s.resolutionNote}`}
          </div>
          {canClose && s.status === "open" && closing?.id !== s.id && (
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              {s.newerVersion && <button type="button" onClick={() => { setClosing({ id: s.id, action: "addressed" }); setNote(""); }} style={smallBtn}>Mark addressed</button>}
              <button type="button" onClick={() => { setClosing({ id: s.id, action: "dismissed" }); setNote(""); }} style={smallBtn}>Dismiss</button>
            </div>
          )}
          {closing?.id === s.id && (
            <div style={{ marginTop: 6 }}>
              <label style={text.hint} htmlFor={`psf-note-${s.id}`}>{closing.action === "addressed" ? "How the new version addresses it" : "Why it is dismissed"}</label>
              <textarea id={`psf-note-${s.id}`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={{ ...inputStyle, width: "100%", marginTop: 4 }} />
              {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)" }}>{error}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button type="button" disabled={busy} onClick={() => void close()} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }}>{closing.action === "addressed" ? "Mark addressed" : "Dismiss"}</button>
                <button type="button" onClick={() => setClosing(null)} style={smallBtn}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
